import type { ApolloClient } from '@apollo/client';
import {
  type LeadAttributionCandidate,
  extractMergeableAttribution,
  mergePreservingExisting,
  pickBestLeadAttribution,
} from 'lib/attribution-display';

import {
  getClientLeadAttribution,
  getLeadAttributionByEmail,
} from 'queries/clients';

function normalizeEmail(value: unknown): string | null {
  if (typeof value !== 'string') return null;
  const trimmed = value.trim().toLowerCase();
  return trimmed.length > 0 ? trimmed : null;
}

function dedupeLeads(
  leads: LeadAttributionCandidate[],
): LeadAttributionCandidate[] {
  const seen = new Set<string>();
  const out: LeadAttributionCandidate[] = [];
  for (const lead of leads) {
    const key = lead.id || `${lead.created_at || ''}`;
    if (!key || seen.has(key)) continue;
    seen.add(key);
    out.push(lead);
  }
  return out;
}

/**
 * Build enrollment.client_data by merging sources without wiping values:
 * 1) explicit metadata from the lead/call UI
 * 2) looked-up lead attribution (gap-fill only)
 * 3) operational fields always win
 */
export function buildEnrollmentClientData(opts: {
  metadata?: Record<string, unknown> | null;
  leadAttribution?: Record<string, unknown> | null;
  operational: Record<string, unknown>;
}): Record<string, unknown> {
  const fromMetadata =
    opts.metadata &&
    typeof opts.metadata === 'object' &&
    !Array.isArray(opts.metadata)
      ? opts.metadata
      : {};
  const merged = mergePreservingExisting(
    fromMetadata,
    extractMergeableAttribution(opts.leadAttribution),
  );

  return {
    ...merged,
    ...opts.operational,
  };
}

/** Persistable merge: fill empty attribution keys on existing client_data. */
export function mergeLeadAttributionIntoClientData(
  clientData: Record<string, unknown> | null | undefined,
  leadMetadata: Record<string, unknown> | null | undefined,
): Record<string, unknown> {
  return mergePreservingExisting(
    clientData,
    extractMergeableAttribution(leadMetadata),
  );
}

export function clientDataAttributionChanged(
  before: Record<string, unknown> | null | undefined,
  after: Record<string, unknown>,
): boolean {
  return JSON.stringify(before || {}) !== JSON.stringify(after);
}

export async function fetchBestLeadAttribution(
  client: ApolloClient<object>,
  opts: {
    userIds?: Array<string | null | undefined>;
    emails?: Array<string | null | undefined>;
    before?: string | null;
  },
): Promise<LeadAttributionCandidate | null> {
  const userIds = Array.from(
    new Set(
      (opts.userIds || []).filter(
        (id): id is string => typeof id === 'string' && id.length > 0,
      ),
    ),
  );
  const emails = Array.from(
    new Set(
      (opts.emails || [])
        .map(normalizeEmail)
        .filter((email): email is string => Boolean(email)),
    ),
  );

  const results = await Promise.all([
    ...userIds.map((userId) =>
      client.query({
        query: getClientLeadAttribution,
        variables: { userId },
        fetchPolicy: 'network-only',
      }),
    ),
    ...emails.map((email) =>
      client.query({
        query: getLeadAttributionByEmail,
        variables: { email },
        fetchPolicy: 'network-only',
      }),
    ),
  ]);

  const leads = dedupeLeads(
    results.flatMap((result) => result.data?.lead || []),
  );

  return pickBestLeadAttribution(leads, opts.before);
}
